import React, { useCallback, useEffect, useState } from 'react';
import tenantPassportService from '../services/tenantPassportService';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import { formatDate } from '../utils/formatters';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const ShareModal = ({ passport, onClose, onShared }) => {
  const [organisationId, setOrganisationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await tenantPassportService.sharePassport(passport.id, organisationId);
      onShared();
    } catch (err) {
      setError(messageFrom(err, 'This could not be shared.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Share this passport">
      <form onSubmit={submit}>
        <p className="ap-muted">
          Enter the organisation ID the landlord or agent gave you. Sharing does not send your documents — it gives
          that organisation access to this passport's verified findings until you revoke it.
        </p>
        {error && <Alert type="error" message={error} />}
        <div className="form-group">
          <label className="form-label">Organisation ID</label>
          <input className="form-control" value={organisationId} onChange={(e) => setOrganisationId(e.target.value)} required />
        </div>
        <button type="submit" className="btn-primary" disabled={busy || !organisationId}>{busy ? 'Sharing…' : 'Share'}</button>
      </form>
    </Modal>
  );
};

const PassportCard = ({ passport, onRevoke, onReload, busy }) => {
  const [sharing, setSharing] = useState(false);
  const activeShares = (passport.shared_with || []).filter((s) => !s.revoked_at);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <strong>{passport.name}</strong>
          <span className={`status-badge status-badge--${passport.status === 'active' ? 'success' : 'danger'}`}>{passport.status}</span>
        </div>
        <p className="ap-muted" style={{ marginTop: 4 }}>
          {passport.status === 'active'
            ? `Valid until ${formatDate(passport.expires_at)}`
            : `Revoked${passport.revocation_reason ? `: ${passport.revocation_reason}` : ''}`}
        </p>
        <p className="ap-muted">Built from {(passport.source_application_ids || []).length} decided application(s).</p>

        {activeShares.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <strong style={{ fontSize: 13 }}>Shared with:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {activeShares.map((s) => (
                <li key={s.recipient_org_id} style={{ fontSize: 13 }}>
                  Organisation #{s.recipient_org_id}
                  <button
                    type="button"
                    className="btn-action"
                    style={{ marginLeft: 8 }}
                    onClick={() => tenantPassportService.revokePassportShare(passport.id, s.recipient_org_id).then(onReload)}
                  >
                    Stop sharing
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {passport.status === 'active' && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="button" className="btn-secondary" onClick={() => setSharing(true)}>Share with a landlord</button>
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => onRevoke(passport.id)}>Revoke</button>
          </div>
        )}
      </div>
      {sharing && <ShareModal passport={passport} onClose={() => setSharing(false)} onShared={() => { setSharing(false); onReload(); }} />}
    </div>
  );
};

const TenantPassportPage = () => {
  const [passports, setPassports] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    tenantPassportService
      .getMyPassports()
      .then(setPassports)
      .catch((err) => setError(messageFrom(err, 'Your passports could not be loaded.')));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    setNotice(null);
    try {
      await tenantPassportService.createPassport();
      setNotice({ type: 'success', message: 'Passport created.' });
      load();
    } catch (err) {
      // The common case: no application has reached a recorded decision yet.
      setNotice({ type: 'error', message: messageFrom(err, 'A passport could not be created yet.') });
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    const reason = window.prompt('Why are you revoking this passport? (shown in your own history only)');
    if (!reason) return;
    setBusyId(id);
    try {
      await tenantPassportService.revokePassport(id, reason);
      load();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This could not be revoked.') });
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <Alert type="error" message={error} />;
  if (!passports) return <LoadingSpinner />;

  return (
    <div className="passport-page" style={{ padding: '20px' }}>
      <h1>My Tenant Passports</h1>
      <p className="ap-muted">
        A passport packages the verified findings from an application that already reached a decision, so you can
        share proven results with a new landlord instead of applying from scratch.
      </p>

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {passports.length === 0 ? (
        <div className="card">
          <div className="card-body text-center">
            <h2>Create Your Digital Passport</h2>
            <p>Package your verified data to share instantly with prospective landlords.</p>
            <button className="btn-primary" style={{ marginTop: '16px' }} onClick={create} disabled={creating}>
              {creating ? 'Compiling…' : 'Compile Passport'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {passports.map((p) => (
            <PassportCard key={p.id} passport={p} onRevoke={revoke} onReload={load} busy={busyId === p.id} />
          ))}
          <button className="btn-secondary" onClick={create} disabled={creating}>{creating ? 'Compiling…' : 'Compile another passport'}</button>
        </>
      )}
    </div>
  );
};

export default TenantPassportPage;
